/**
 * Single source of truth for which admin routes require which gates.
 *
 * Today every route is SUPER_ADMIN-only. Phase K (deferred) flips this
 * matrix to add SystemRole.SALES to allowedRoles for non-destructive pages
 * without touching App.tsx — that PR becomes a single-line edit per row.
 *
 * Phase B already ensured zero users have `users:manage` permission AND
 * `system_role != SUPER_ADMIN` in production (verified via SUPABASE_DB_URL).
 * So tightening the route gate from portal's old `requiredPermissions:
 * [{users, manage}]` to `requiredSystemRoles: [SUPER_ADMIN]` is observably a
 * no-op for current users.
 */

import { SystemRole } from '@vehix/portal/types';

export type AdminPage =
  | 'dashboard'
  | 'setup-tenants'
  | 'setup-users'
  | 'setup-zones'
  | 'setup-zone-list'
  | 'setup-configurations'
  | 'workflow-management'
  | 'pipelines'
  | 'monitoring-data-quality'
  | 'monitoring-system-health'
  | 'sales-dashboard'
  | 'analytics'
  | 'feedback'
  | 'api-docs';

interface PageGate {
  allowedRoles: SystemRole[];
}

const ADMIN_RBAC: Record<AdminPage, PageGate> = {
  dashboard: { allowedRoles: [SystemRole.SUPER_ADMIN] },
  'setup-tenants': { allowedRoles: [SystemRole.SUPER_ADMIN] },
  'setup-users': { allowedRoles: [SystemRole.SUPER_ADMIN] },
  // 'setup-zones' is the Map view; 'setup-zone-list' is the tabular view.
  'setup-zones': { allowedRoles: [SystemRole.SUPER_ADMIN] },
  'setup-zone-list': { allowedRoles: [SystemRole.SUPER_ADMIN] },
  // Configurations is a placeholder today; future content will be system-
  // wide feature flags, integration toggles, and platform-level defaults.
  'setup-configurations': { allowedRoles: [SystemRole.SUPER_ADMIN] },
  'workflow-management': { allowedRoles: [SystemRole.SUPER_ADMIN] },
  // 'pipelines' covers /workflow/pipelines (Runs and Schedules sub-tabs) and
  // /workflow/pipelines/:id — single gate because they are the same Pipeline
  // Orchestrator feature. /workflow/pipelines/schedules is now an unguarded
  // redirect into the Schedules sub-tab, which this gate then covers.
  pipelines: { allowedRoles: [SystemRole.SUPER_ADMIN] },
  // Monitoring section — both pages are placeholders today; real content
  // (data-quality signals, platform-health metrics) is deferred.
  'monitoring-data-quality': { allowedRoles: [SystemRole.SUPER_ADMIN] },
  'monitoring-system-health': { allowedRoles: [SystemRole.SUPER_ADMIN] },
  // Sales section — placeholder for the upcoming sales dashboard.
  'sales-dashboard': { allowedRoles: [SystemRole.SUPER_ADMIN] },
  analytics: { allowedRoles: [SystemRole.SUPER_ADMIN] },
  feedback: { allowedRoles: [SystemRole.SUPER_ADMIN] },
  'api-docs': { allowedRoles: [SystemRole.SUPER_ADMIN] }
};

export function gateFor(page: AdminPage): { requiredSystemRoles: SystemRole[] } {
  return { requiredSystemRoles: ADMIN_RBAC[page].allowedRoles };
}
